import type {
  DashboardResponseDTO,
} from "../types/dashboard.dto";


export interface DashboardData {

  representative: {
    id: string;
    displayName: string;
    avatarUrl: string;
    role: string;
    isOnline: boolean;
  };


  stats: Array<{
    id: string;
    icon: any;
    label: string;
    value: number;
    format: any;
    subtitle: string;
    trend: {
      direction: "up" | "down" | "flat";
      percent: number;
      label: string;
    };
    accent: any;
  }>;


  dailySales: any[];

  weeklyCustomers: any[];

  recentOrders: any[];

  recentTickets: any[];

  quickActions: any[];

  timeline: any[];

}


/**
 * Best-effort numeric coercion.
 *
 * The backend serializes some totals (e.g. `total_sales_amount`) as
 * numeric strings, and every field here is technically optional from
 * the frontend's point of view (a defensive boundary against the API
 * response shape drifting) -- so `undefined`/`null`/non-numeric input
 * all safely fall back to `0` instead of producing `NaN`, which would
 * otherwise propagate into `StatCard`/`useCountUp` and break rendering.
 */
function toSafeNumber(value: unknown): number {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}


export function mapDashboardSummary(
  dto: DashboardResponseDTO
): DashboardData {

  // Defensive: the real API response is flat (no `dashboard` envelope,
  // see dashboard.dto.ts), but we still guard against a missing/partial
  // payload rather than assuming every field is always present.
  const representativeInfo = dto?.representative;

  const totalCustomers = toSafeNumber(dto?.total_customers);
  const totalOrders = toSafeNumber(dto?.total_orders);
  const totalSalesAmount = toSafeNumber(dto?.total_sales_amount);
  const pendingCommission = toSafeNumber(dto?.pending_commission);

  return {

    representative: {

      id: String(representativeInfo?.tenant_id ?? representativeInfo?.id ?? ""),

      displayName: representativeInfo?.mobile ?? "نماینده",

      avatarUrl: "",

      role: "representative",

      isOnline: true,

    },


    stats: [


      {

        id: "active-customers",

        icon: "customers",

        label: "مشتریان",

        value: totalCustomers,

        format: "number",

        subtitle: "تعداد مشتریان",

        trend: {

          direction: "flat",

          percent: 0,

          label: "",

        },

        accent: "violet",

      },


      {

        id: "orders",

        icon: "orders",

        label: "سفارش‌ها",

        value: totalOrders,

        format: "number",

        subtitle: "کل سفارش‌ها",

        trend: {

          direction: "flat",

          percent: 0,

          label: "",

        },

        accent: "sky",

      },


      {

        id: "sales",

        icon: "sales",

        label: "درآمد",

        value: totalSalesAmount,

        format: "currency",

        subtitle: "مجموع درآمد",

        trend: {

          direction: "flat",

          percent: 0,

          label: "",

        },

        accent: "amber",

      },


      // Backend has no `services_count` field (removed from the DTO --
      // see dashboard.dto.ts), so that stat is intentionally omitted
      // rather than rendered with a fabricated/mock value.
      //
      // `wallet-balance` (consumed by DashboardPage's hero KPI row) is
      // backed by the real `pending_commission` total, since that's
      // the closest real balance figure the API actually returns.
      {

        id: "wallet-balance",

        icon: "wallet",

        label: "کیف پول",

        value: pendingCommission,

        format: "currency",

        subtitle: "کمیسیون در انتظار پرداخت",

        trend: {

          direction: "flat",

          percent: 0,

          label: "",

        },

        accent: "rose",

      },


    ],



    // None of these are returned by GET /api/representative/dashboard
    // today. Defaulting to `[]` (rather than crashing on missing
    // fields) lets every consuming component's own `?? []` /
    // `.length` guards render an empty state instead of throwing.
    dailySales: Array.isArray((dto as any)?.dailySales) ? (dto as any).dailySales : [],
    weeklyCustomers: Array.isArray((dto as any)?.weeklyCustomers) ? (dto as any).weeklyCustomers : [],
    recentOrders: Array.isArray((dto as any)?.recentOrders) ? (dto as any).recentOrders : [],
    recentTickets: Array.isArray((dto as any)?.recentTickets) ? (dto as any).recentTickets : [],
    quickActions: Array.isArray((dto as any)?.quickActions) ? (dto as any).quickActions : [],
    timeline: Array.isArray(dto?.recent_activity) ? dto.recent_activity : [],


  };

}
